import { Button } from '@jouzen/ecom-components/v3/Button';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Motion from '@/app/components/Motion';
import SectionHeading from '@/app/components/SectionHeading';

import HsaFsaIcon from './common/HsaFsaIcon';

export interface HeroTemplateProps {
  readonly background: ReactNode;
  readonly containerClassName?: string;
  readonly ctaProps: {
    className: string;
    labelKey: MessageKey;
    onClick?: () => Promise<void>;
  };
  readonly heroName: string;
  readonly sectionClassName?: string;
  readonly subtitle: ReactNode;
  readonly tag?: ReactNode;
  readonly title: MessageKey;
}

const HeroTemplate = ({
  background,
  containerClassName,
  ctaProps,
  heroName,
  sectionClassName = 'h-[calc(100svh-70px)] md:h-[calc(100svh-49px)]',
  subtitle,
  tag,
  title,
}: HeroTemplateProps) => {
  const t = useTranslations();
  return (
    <section
      className={cx('bg-sandstone-200 lg:pb-4', sectionClassName)}
      data-cy={`${heroName}-hero`}
    >
      <LayoutGrid className={cx('h-full', containerClassName)}>
        <GridItem
          className="h-full overflow-hidden max-lg:bg-black! lg:mx-5 lg:rounded-b-xl"
          colEnd={{ sm: 'full' }}
          colStart={{ sm: 'full' }}
          rowEnd={{ sm: 2 }}
          rowStart={{ sm: 1 }}
        >
          {background}
        </GridItem>
        <GridItem
          className="z-20 clip-polygon md:relative lg:mx-5"
          colEnd={{ sm: 'full' }}
          colStart={{ sm: 'full' }}
          rowEnd={{ sm: 2 }}
          rowStart={{ sm: 1 }}
        >
          <div
            className="absolute top-0 left-0 h-svh w-full lg:-top-12 lg:bottom-0 lg:h-auto"
            data-cy={`${heroName}-hero-background`}
          >
            <LayoutGrid
              className="h-full content-end! lg:px-15 lg:pb-4"
              rowGap={0}
            >
              <GridItem
                colEnd={{ sm: 'main' }}
                colStart={{ sm: 'main', md: 'main', lg: 1 }}
              >
                <Motion
                  initial={{
                    opacity: 0,
                    translateY: 25,
                    filter: 'blur(5px)',
                  }}
                  animate={{ opacity: 1, translateY: 0, filter: 'blur(0)' }}
                  transition={{ duration: 1.25, type: 'tween', delay: 1 }}
                >
                  <div className="mt-24 mb-5 lg:mb-22">
                    {tag}
                    <SectionHeading
                      content={title}
                      emProps={{
                        className: 'block pt-2 lg:pt-4',
                      }}
                      typographyProps={{
                        className:
                          'leading-tighter text-center text-5xl md:text-6xl lg:text-7xl xl:text-8xl xxl:text-9xl lg:text-left', // TODO: design has 120px as largest, but our sizes only go up to 96px
                        font: 'serif',
                        weight: 'light',
                        color: 'light',
                        Element: 'h1',
                        variant: 'h1',
                      }}
                    />
                  </div>
                </Motion>
              </GridItem>
              <GridItem
                className="flex flex-col items-center self-end lg:block"
                colEnd={{ sm: 'main', md: 20, lg: 10, xl: 8 }}
                colStart={{ sm: 'main', md: 6, lg: 1, xl: 1 }}
              >
                <Motion
                  className="w-full lg:w-auto"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 2, type: 'tween', delay: 1.25 }}
                >
                  <Motion
                    animate={{ opacity: 1 }}
                    data-cy={`${heroName}-hero-content`}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 1, type: 'tween' }}
                  >
                    <Typography
                      align="centerLgLeft"
                      className="mb-4 text-xl lg:mb-6"
                      color="light"
                      data-cy="home-hero-content-copy"
                      Element="p"
                      variant="body-large"
                    >
                      {subtitle}
                    </Typography>
                    <div className="mb-5 flex w-full flex-col items-center space-y-3 lg:mb-12 lg:items-start">
                      <div className="mb-4 flex justify-center lg:hidden">
                        <HsaFsaIcon />
                      </div>
                      <Button
                        href="/store/rings"
                        className={ctaProps.className}
                        data-cy={`${heroName}-hero-content-button`}
                        variant="primary"
                        onClick={ctaProps.onClick}
                      >
                        {t(ctaProps.labelKey)}
                      </Button>
                      <div className="hidden justify-center lg:flex">
                        <HsaFsaIcon />
                      </div>
                    </div>
                  </Motion>
                </Motion>
              </GridItem>
            </LayoutGrid>
          </div>
        </GridItem>
      </LayoutGrid>
    </section>
  );
};

export default HeroTemplate;
